import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listEmoluments, removeEmolument, setEmolumentActive } from "../services/payment";
import { listFrequencies } from "../services/teacher";
import { levelKey } from "../utils/classesLabels";
import { useConfirm } from "../hook/useConfirm";
import { canRemoveTaxEmolument } from "../services/auth";

// Split out of the former TaxasLayer. Same "hide cadastro from the menu"
// philosophy applied to ListaProdutosLayer: cadastro is only reachable via
// the "Adicionar Taxa" button below (see CadastrarTaxaLayer / App.js route
// `/pagamentos/taxas/cadastrar`).
const ListaTaxasLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { confirm, ConfirmModal } = useConfirm();
  const [frequencies, setFrequencies] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchTaxes = () => {
    setLoading(true);
    listEmoluments()
      .then((resp) => setTaxes(resp.data || []))
      .catch(() => toast.error(t("taxa.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
    fetchTaxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const courseName = (tax) => tax.course?.description || frequencies.find((f) => f.id === tax.course?.id)?.description || "-";

  const toggleActive = async (tax) => {
    const nextActive = tax.active === "0" || tax.active === 0 ? 1 : 0;
    setBusyId(tax.id);
    try {
      await setEmolumentActive(tax.id, nextActive);
      toast.success(nextActive ? t("taxa.success.activated") : t("taxa.success.deactivated"));
      fetchTaxes();
    } catch (err) {
      toast.error(t("taxa.error.toggle"));
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (tax) => {
    if (!(await confirm(t("taxa.confirm.remove")))) return;
    setBusyId(tax.id);
    try {
      await removeEmolument(tax.id);
      toast.success(t("taxa.success.removed"));
      fetchTaxes();
    } catch (err) {
      const apiError = err?.response?.data?.error;
      toast.error(typeof apiError === "string" ? apiError : t("taxa.error.remove"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      {ConfirmModal}

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("taxa.section.list")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("taxa.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/pagamentos/taxas/cadastrar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("taxa.list.add")}
              </button>
            </div>

            {loading ? (
              <div className='text-center py-40'>{t("global.loading")}</div>
            ) : taxes.length === 0 ? (
              <div className='alert alert-info mb-0'>{t("taxa.hint.noTaxes")}</div>
            ) : (
              <div className='table-responsive scroll-sm'>
                <table className='table bordered-table mb-0'>
                  <thead>
                    <tr>
                      <th scope='col'>{t("taxa.field.name")}</th>
                      <th scope='col'>{t("taxa.field.value")}</th>
                      <th scope='col'>{t(levelKey("taxa.field.course"))}</th>
                      <th scope='col'>{t("global.status")}</th>
                      <th scope='col'>{t("turma.col.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taxes.map((tax) => {
                      const isActive = !(tax.active === "0" || tax.active === 0);
                      return (
                        <tr key={tax.id}>
                          <td className='fw-semibold text-primary-light'>{tax.taxName}</td>
                          <td>{tax.taxValue}</td>
                          <td>{courseName(tax)}</td>
                          <td>
                            <span className={isActive ? "badge bg-success-focus text-success-main" : "badge bg-neutral-200 text-neutral-600"}>
                              {isActive ? t("global.active") : t("global.inactive")}
                            </span>
                          </td>
                          <td>
                            <div className='d-flex align-items-center gap-2'>
                              <button
                                type='button'
                                className={`btn btn-sm d-flex align-items-center gap-1 ${isActive ? "btn-outline-danger-600" : "btn-outline-success-600"}`}
                                onClick={() => toggleActive(tax)}
                                disabled={busyId === tax.id}
                              >
                                <Icon icon={isActive ? "mdi:close-circle-outline" : "mdi:check-circle-outline"} />
                                {isActive ? t("global.deactivate") : t("global.activate")}
                              </button>
                              {canRemoveTaxEmolument() && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-outline-danger-600 d-flex align-items-center gap-1'
                                  onClick={() => handleRemove(tax)}
                                  disabled={busyId === tax.id}
                                >
                                  <Icon icon='mdi:trash-can-outline' />
                                  {t("global.delete")}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaTaxasLayer;
